import React from "react";
import { createPortal } from "react-dom";
import type { CardInstance, OwnedCardTile } from "../types/cardTypes";
import RespectCard from "./RespectCard";
import CardStatusChip from "./CardStatusChip";
import CardExpiryPill from "./CardExpiryPill";
import {
  handChipKind,
  historyChipKind,
  isInHand,
  playAffordance,
} from "../lib/cardState";
import { expiryReadout, formatWallClockEnd } from "../lib/expiry";
import { useNowTicker } from "../hooks/useNowTicker";
import { cardOriginLine } from "../lib/cardOrigin";
import "../styles/cards.css";
import "../styles/cardsDeck.css";

export interface CardTileProps {
  tile: OwnedCardTile;
  /**
   * All in_hand CardInstance rows for THIS card (from /my-cards `cards[]`),
   * used to enumerate per-copy expiries when there is more than one copy.
   */
  handInstances?: CardInstance[];
  /**
   * Called with the tile when the user taps Play; the parent resolves
   * `tile.playableInstanceId` to the modal target. Omit for a read-only tile.
   */
  onPlay?: (tile: OwnedCardTile) => void;
  /** When false, no Play button and no expand overlay (catalog view). */
  interactive?: boolean;
}

function formatDeadline(iso: string | undefined): string | null {
  if (!iso) return null;
  const end = formatWallClockEnd(iso);
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return end ? `${day} ${end}` : day;
  } catch {
    return end;
  }
}

/**
 * Grid unit for the hand + history sections. Always
 * shows the card face, an honest status chip and expiry pill, and (for an
 * in-hand tile) a >=44px Play button that renders the five playability states.
 * xN is `inHandCount`, never lifetime `totalEverOwned`. Multi-copy tiles expand
 * to list each copy's hand-expiry.
 */
export default function CardTile({
  tile,
  handInstances,
  onPlay,
  interactive = true,
}: CardTileProps) {
  const now = useNowTicker(30_000);
  const [expanded, setExpanded] = React.useState(false);

  const def = tile.definition;
  const inHand = isInHand(tile);
  const chipKind = inHand ? handChipKind(tile) : historyChipKind(tile);
  const affordance = inHand ? playAffordance(tile.playability) : { kind: "none" as const };

  const badgeCount = tile.inHandCount;
  const multiCopy = inHand && tile.inHandCount > 1;

  React.useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  const perCopyExpiries = React.useMemo(() => {
    const rows = (handInstances ?? [])
      .filter((c) => c.status === "in_hand")
      .map((c) => ({
        id: c._id,
        readout: expiryReadout(c.unplayedExpiresAt ?? null, now),
      }));
    rows.sort((a, b) => {
      const av = a.readout?.deltaMs ?? Number.POSITIVE_INFINITY;
      const bv = b.readout?.deltaMs ?? Number.POSITIVE_INFINITY;
      return av - bv;
    });
    return rows;
  }, [handInstances, now]);

  const renderPlayButton = () => {
    if (!interactive || !onPlay) return null;
    switch (affordance.kind) {
      case "ready":
      case "needs_target":
        return (
          <button
            type="button"
            className="card-tile__play"
            onClick={() => onPlay(tile)}
            disabled={!def}
          >
            Play
          </button>
        );
      case "play_before": {
        const deadline = formatDeadline(affordance.deadline);
        return (
          <button
            type="button"
            className="card-tile__play"
            onClick={() => onPlay(tile)}
            disabled={!def}
            title={deadline ? `Play before ${deadline}` : undefined}
          >
            Play{deadline ? ` · before ${deadline}` : ""}
          </button>
        );
      }
      case "blocked":
        return (
          <button
            type="button"
            className="card-tile__play card-tile__play--blocked"
            disabled
            title={affordance.code ? `Can't play: ${affordance.code}` : undefined}
          >
            {blockedLabel(affordance.code)}
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`card-tile${!inHand ? " card-tile--history" : ""}`}>
      {badgeCount > 1 ? <span className="card-tile__count">×{badgeCount}</span> : null}

      <RespectCard card={def} size="xs" showDetails expandable={interactive} />

      <div className="card-tile__meta">
        <CardStatusChip kind={chipKind} />

        {inHand ? (
          <CardExpiryPill mode="hand" expiresAt={tile.soonestUnplayedExpiresAt} />
        ) : tile.effect && tile.effect.state !== "none" ? (
          <CardExpiryPill
            mode="effect"
            effect={tile.effect}
            lifetime={def?.effectLifetime ?? null}
          />
        ) : null}

        {tile.origin ? <span className="card-tile__origin">{cardOriginLine(tile)}</span> : null}
      </div>

      {renderPlayButton()}

      {interactive && multiCopy ? (
        <button
          type="button"
          className="card-tile__more"
          onClick={() => setExpanded(true)}
          aria-haspopup="dialog"
        >
          {tile.inHandCount} copies · see expiries
        </button>
      ) : null}

      {expanded
        ? createPortal(
            <div
              className="card-tile-copies"
              role="dialog"
              aria-modal="true"
              aria-label={`${def?.name || "Card"} copies in hand`}
            >
              <button
                type="button"
                className="card-tile-copies__backdrop"
                aria-label="Close"
                onClick={() => setExpanded(false)}
              />
              <div className="card-tile-copies__panel">
                <h4 className="card-tile-copies__title">
                  {def?.emoji} {def?.name} — {tile.inHandCount} in hand
                </h4>
                <ul className="card-tile-copies__list">
                  {perCopyExpiries.map((row, i) => (
                    <li key={row.id} className="card-tile-copies__row">
                      <span>Copy {i + 1}</span>
                      <span
                        className={`card-tile-copies__expiry card-tile-copies__expiry--${
                          row.readout?.urgency ?? "calm"
                        }`}
                      >
                        {row.readout
                          ? row.readout.expired
                            ? "expired"
                            : `expires in ${row.readout.label}`
                          : "no expiry"}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="card-tile-copies__close"
                  onClick={() => setExpanded(false)}
                >
                  Close
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function blockedLabel(code: string | undefined): string {
  switch (code) {
    case "too_late":
      return "Too late today";
    case "double_stacked":
      return "Already active";
    case "recent_aotd_author":
      return "You've been winning";
    case "no_race_today":
      return "No race yet";
    case "race_undecided":
      return "Race not decided";
    case "did_not_race":
      return "You didn't race";
    case "won_today":
      return "You won today";
    case "not_close_enough":
      return "Not close enough";
    default:
      return "Can't play now";
  }
}
